import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import * as API from "../../lib/api.js";
import LayoutSettings from "../Layout/LayoutSettings.jsx";
import DesigningLayout from "./DesigningLayout.jsx";
import WizardCheck from "./WizardCheck.js";
import WizardLogotype from "./WizardLogotype.jsx";
import { PartListDefault, PartListECommerce, PartListInit } from "./WizardParts.js";

const { MultipleCheck } = WizardCheck;

export default function WizardFormNew(props) {
    const { t } = useTranslation();

    const history = useHistory();

    let {
        startPart,
        currentPart,
        setCurrentPart,
        variables,
        setVariables,
        structure,
        setStructure,
        reset,

        isTesting, //
    } = props;

    const refWizard = React.useRef(null);

    const [showCustom, setShowCustom] = React.useState(false);
    const [currentPartList, setPartList] = React.useState(PartListInit);

    // Generate Promises for Designing Layout
    const [dlPromises, setDLPromises] = React.useState([]);

    // Google Target Constants ---------------------------------------------- //
    const [GTC, setGTC] = React.useState([]);

    React.useEffect(() => {
        if (GTC.length === 0) {
            API.GoogleGetTargetConstants().then(({ list }) => setGTC(list));
        }
    }, [GTC]);

    // ---------------------------------------------------------------------- //
    React.useEffect(() => {
        if (variables.ProductType) {
            const pl = partListByProductType(variables.ProductType);
            currentPartList !== pl && setPartList(pl);
        }
    }, [variables]);

    // ---------------------------------------------------------------------- //

    let [abort, setAbort] = React.useState(null);

    function generate() {
        return compile().then(gotoLastPart);
    }

    function addDLPromise(promise) {
        setDLPromises([...dlPromises, promise]);
        return promise;
    }

    function gotoLastPart() {
        setCurrentPart(...currentPartList.slice(-1));
    }

    if (typeof currentPart === "string") {
        currentPart = currentPartList.filter(({ key }) => key === currentPart)[0];
        currentPart = currentPart || props.startStep || currentPartList[0];
    } else if (!currentPart) {
        currentPart = currentPart || props.startStep || currentPartList[0];
    }

    function save(data) {
        abort && abort.abort();
        setAbort((abort = new AbortController()));
        return new Promise(resolve =>
            API.WizardSave(data, { signal: abort.signal })
                .then(value => {
                    setAbort(null);
                    resolve(value);
                })
                .catch(() => {})
        );
    }

    function isCurrentPart(targetPart) {
        return targetPart === currentPart;
    }

    function isPrevPart(targetPart) {
        for (const part of currentPartList) {
            if (part === currentPart) {
                return false;
            }

            if (part === targetPart) {
                return true;
            }
        }
    }

    function isEnd() {
        return currentPart === currentPartList[currentPartList.length - 1];
    }

    function partIndex(targetPart) {
        return currentPartList.indexOf(targetPart);
    }

    function partListByProductType(productType) {
        // Set wizard part list by ProductType
        if (productType === "ecommerce") {
            if (currentPartList !== PartListECommerce) {
                return PartListECommerce;
            }
        } else if (productType !== "Custom") {
            if (currentPartList !== PartListDefault) {
                return PartListDefault;
            }
        }

        return currentPartList;
    }

    function back() {
        const indexPrev = partIndex(currentPart) - 1;

        if (indexPrev < 0) {
            history.goBack();
            return;
        }

        const newPart = currentPartList[indexPrev];

        setCurrentPart(newPart);

        save({ partKey: newPart.key, variables }); // ! TESTING
    }

    function next(event, updVariables, updStructure) {
        let vars = variables;

        const pl = partListByProductType(updVariables.ProductType);
        currentPartList !== pl && setPartList(pl);

        // Reset data when ProductType changes
        if (currentPart.key === "product-type" && variables.ProductType !== updVariables.ProductType) {
            vars = reset().variables; // Reset top-level and use
            vars.ProductType = updVariables.ProductType; // Use selected ProductType
            updVariables = vars; // Replace updVariables
        }

        // Detect next wizard part
        const indexNext = Math.min(pl.length - 1, partIndex(currentPart) + 1);
        const newPart = pl[indexNext];

        // Update variables
        const newVariables = { ...vars, ...updVariables };
        setVariables(newVariables);

        // Update structure
        if (updStructure) {
            setStructure(updStructure);
        }

        // Timeout to next wizard part
        setTimeout(() => {
            // Set wizard part
            setCurrentPart(newPart);

            save({ partKey: newPart.key, variables: newVariables }); // ! TESTING
        }, 100);
    }

    function goto(partKey) {
        const newPart = currentPartList.filter(({ key }) => key === partKey)?.[0];

        if (!newPart) {
            console.warn(`Unknown part '${partKey}' in`, currentPartList);
            return;
        }

        setCurrentPart(newPart);

        save({ partKey: newPart.key, variables }); // ! TESTING
    }

    function set(updVariables, updStructure) {
        updVariables && setVariables({ ...variables, ...updVariables });

        if (updStructure) {
            let BlockProject;

            if (Array.isArray(updStructure)) {
                if (updStructure[0].name === "BlockProject") {
                    BlockProject = updStructure[0];
                }
            } else if (updStructure?.name === "BlockProject") {
                BlockProject = updStructure;
            }

            if (updVariables.ProductType === "Custom") {
                BlockProject.structureVirgin = BlockProject.children;
            }

            updStructure && setStructure(updStructure);
        }
    }

    function setStepByIndex(index) {
        const newPart = currentPartList[index];
        setCurrentPart(newPart);

        save({ partKey: newPart.key, variables }); // ! TESTING
    }

    function onFormSubmit(event) {
        event.preventDefault();
    }

    function onFormKeyDown(event) {
        if ((event.ctrlKey || event.metaKey) && event.keyCode === 13) {
            event.preventDefault();
            next();
        }
    }

    async function compile() {
        // TODO - Temporary wizard (mongo) not save variables
        // const data = { partKey: PartList[PartList.length - 1].key, variables };
        const data = { partKey: currentPartList[currentPartList.length - 1].key };

        // ! TODO Find ideal solution
        const _structure = ["ecommerce", "Custom"].includes(variables.ProductType) ? structure : null;

        return (
            save(data, false)
                // Structure only for Custom
                .then(() => API.CompileGenerate({ variables, structure: _structure }))
                .then(async ({ variables, structure }) => {
                    setVariables(variables);
                    setStructure(structure);

                    // TODO - Temporary wizard (mongo) not save variables
                    // return save({ ...data, partKey: PartList[PartList.length - 2].key, variables, structure }, false) //
                    const result = await save(
                        { ...data, partKey: currentPartList[currentPartList.length - 2].key },
                        false
                    ); //

                    if (!result) {
                        throw new Error("Error while saving.");
                    }
                })
                .catch(e => {
                    if (e.name !== "AbortError" && e.message !== "Error while saving.") {
                        console.warn(`[ERROR] onDesigningEnd(): ${e.message} (${e.name})`);
                    }
                })
        );
    }

    function onDesigningStart() {
        props.onDesigningStart && props.onDesigningStart();
    }

    function onDesigningEnd() {
        props.onDesigningEnd && props.onDesigningEnd();
    }

    function CalcComplete() {
        const completeSteps = {};
        for (const part of currentPartList) {
            const pk = currentPartList.indexOf(part);

            completeSteps[pk] = {
                complete: true,
                filled: false,
                fullfilled: false,
                vars: {},
            };

            if (part.requires) {
                const vars = {};

                for (const varName of part.requires) {
                    vars[varName] = variables[varName];
                }

                completeSteps[pk].fullfilled = MultipleCheck(vars, false);
            }
        }

        let allComplete = true;

        return { completeSteps, allComplete };
    }

    const { completeSteps, allComplete } = CalcComplete();

    const partProps = {
        back,
        next,
        goto,
        set,
        setShowCustom,
        generate,
        addDLPromise, // ecommerce
        gotoLastPart, // ecommerce
        variables,
        structure,
        allComplete,
        isTesting,

        GTC,
    };

    const CLWizard = ["wizard", "with-menu"];
    props.hide && CLWizard.push("hide");

    return (
        <>
            <form
                ref={refWizard}
                className={CLWizard.join(" ")}
                action="."
                onSubmit={onFormSubmit}
                onKeyDown={event => {
                    if (completeSteps[partIndex(currentPart)]) {
                        onFormKeyDown(event);
                    }
                }}
                autoComplete="false"
            >
                <div className="wizard-inner">
                    <WizardLogotype show={!isEnd()} center mobile />
                    <WizardLogotype show={!isEnd()} right desktop />

                    <DesigningLayout
                        generatePromises={dlPromises}
                        variables={variables}
                        structure={structure}
                        completeSteps={completeSteps}
                        setStepByIndex={setStepByIndex}
                        onDesigningStart={onDesigningStart}
                        onDesigningEnd={onDesigningEnd}
                        step={null}
                        show={isEnd()}
                        dynamicMode={props.dynamicDesigning}
                        logotype
                        designing
                    />

                    {/* WizardMenu */}
                    <div className="wizard-menu-wrapper">
                        <div className={["wizard-menu", !isEnd() ? "show" : ""].join(" ")}>
                            {currentPartList.map((part, index) => {
                                const Component = part.component;
                                if (!Component) {
                                    return null;
                                }

                                const CN = ["item"];
                                isCurrentPart(part) && CN.push("active");

                                return (
                                    <button
                                        key={part.key}
                                        className={CN.join(" ")}
                                        // onClick={() => setCurrentPart(part.key)}
                                    >
                                        {index + 1}. {t(part.t_wizardMenu)}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {currentPartList.map(part => {
                        const Component = part.component;
                        if (!Component) {
                            return null;
                        }

                        const className = ["wizard-part"];
                        isCurrentPart(part) && className.push("current");
                        isPrevPart(part) && className.push("prev");

                        const spreadPartProps = { ...partProps, partKey: part.key };
                        part.isLast && (spreadPartProps.isLast = true);

                        spreadPartProps.prev = (() => {
                            if (!startPart) {
                                return spreadPartProps.prev;
                            } else if (currentPartList.indexOf(part) > currentPartList.indexOf(startPart)) {
                                return spreadPartProps.prev;
                            } else {
                                return null;
                            }
                        })();

                        return (
                            <div key={part.key} className={className.join(" ")}>
                                <Component {...spreadPartProps} />
                            </div>
                        );
                    })}
                </div>
            </form>

            {showCustom && <LayoutSettings {...partProps} />}
        </>
    );
}
